import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  NativeSelect,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  EyeIcon,
  RefreshCwIcon,
  ShieldAlertIcon,
  TrendingUpIcon,
  UsersIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { cardHover, PageHeader, SectionHeading } from '~/components/shell'
import { useAuth } from '~/contexts/AuthContext'
import { type ActivityLog, activityApi } from '~/lib/api'

export const Route = createFileRoute('/_app/admin/activity')({
  component: ActivityPage,
})

const PAGE_SIZE = 15

function StatCard({
  title,
  value,
  icon: Icon,
  color = 'blue',
}: {
  title: string
  value: string | number
  icon: React.ComponentType<{ size?: number; color?: string }>
  color?: string
}) {
  return (
    <Card.Root p={4}>
      <HStack justify="space-between">
        <VStack align="flex-start" gap={1}>
          <Text fontSize="sm" color="content.secondary">
            {title}
          </Text>
          <Text fontSize="2xl" fontWeight="bold">
            {value}
          </Text>
        </VStack>
        <Box p={3} borderRadius="lg" bg={`${color}.100`} _dark={{ bg: `${color}.900` }}>
          <Icon size={24} color={`var(--chakra-colors-${color}-500)`} />
        </Box>
      </HStack>
    </Card.Root>
  )
}

function TopList({
  title,
  items,
  labelKey,
  valueKey,
  valueLabel = 'views',
  emptyMessage = 'No data',
  onViewAll,
}: {
  title: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any[]
  labelKey: string
  valueKey: string
  valueLabel?: string
  emptyMessage?: string
  onViewAll?: () => void
}) {
  return (
    <Card.Root
      cursor={onViewAll ? 'pointer' : 'default'}
      onClick={onViewAll}
      _hover={onViewAll ? cardHover : undefined}
      transition="all 0.3s"
    >
      <Card.Header>
        <Flex justify="space-between" align="center">
          <Heading size="sm">{title}</Heading>
          {onViewAll && (
            <Text fontSize="xs" color="blue.500" fontWeight="medium">
              View All
            </Text>
          )}
        </Flex>
      </Card.Header>
      <Card.Body pt={0}>
        {items.length === 0 ? (
          <Text color="content.secondary" fontSize="sm">
            {emptyMessage}
          </Text>
        ) : (
          <VStack align="stretch" gap={2}>
            {items.map((item, index) => (
              <Flex
                key={index}
                justify="space-between"
                align="center"
                py={2}
                borderColor="border"
                borderBottomWidth={index < items.length - 1 ? '1px' : '0'}
              >
                <HStack gap={2}>
                  <Badge
                    colorPalette={index === 0 ? 'yellow' : index === 1 ? 'gray' : 'orange'}
                    variant="subtle"
                    px={2}
                  >
                    #{index + 1}
                  </Badge>
                  <Text
                    fontSize="sm"
                    maxW="200px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {String(item[labelKey])}
                  </Text>
                </HStack>
                <Badge colorPalette="blue" variant="subtle">
                  {String(item[valueKey])} {valueLabel}
                </Badge>
              </Flex>
            ))}
          </VStack>
        )}
      </Card.Body>
    </Card.Root>
  )
}

function ActivityPage() {
  const { hasMinRole, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  const [statsDays, setStatsDays] = useState(7)

  const isAdmin = hasMinRole('admin')

  // Redirect non-admins after a short delay
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      const timer = setTimeout(() => {
        navigate({ to: '/' })
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [authLoading, isAdmin, navigate])

  // Show access denied for non-admins
  if (!authLoading && !isAdmin) {
    return (
      <Flex h="100%" align="center" justify="center">
        <VStack gap={4} textAlign="center" p={8}>
          <Box p={4} borderRadius="full" bg="red.100" _dark={{ bg: 'red.900' }}>
            <ShieldAlertIcon size={48} color="var(--chakra-colors-red-500)" />
          </Box>
          <Heading size="lg" color="red.500">
            Access Denied
          </Heading>
          <Text color="content.secondary" maxW="400px">
            This page is restricted to <strong>admin</strong> users only.
          </Text>
          <Text fontSize="sm" color="content.secondary">
            Redirecting to home page...
          </Text>
        </VStack>
      </Flex>
    )
  }

  // Fetch activity stats
  const {
    data: statsData,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ['activity-stats', statsDays],
    queryFn: () => activityApi.stats(statsDays),
    retry: false,
  })

  // Fetch activity logs
  const {
    data: logsData,
    isLoading: logsLoading,
    refetch: refetchLogs,
  } = useQuery({
    queryKey: ['activity-logs', currentPage],
    queryFn: () => activityApi.list({ page: currentPage, pageSize: PAGE_SIZE }),
    retry: false,
  })

  const stats = statsData
  const logs = logsData?.logs || []
  const totalLogs = logsData?.total || 0
  const totalPages = Math.ceil(totalLogs / PAGE_SIZE)

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const formatPath = (path: string) => {
    // Make paths more readable
    if (path === '/') return 'Home'
    return path
      .replace(/^\//, '')
      .replace(/\//g, ' → ')
      .replace(/-/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="User Activity"
        subtitle="Track and analyze user page visits"
        actions={
          <>
            <NativeSelect.Root size="sm" w="160px">
              <NativeSelect.Field
                value={statsDays}
                onChange={(e) => setStatsDays(Number(e.target.value))}
              >
                <option value={1}>Last 24 hours</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </NativeSelect.Field>
            </NativeSelect.Root>
            <Button
              size="sm"
              onClick={() => {
                refetchStats()
                refetchLogs()
              }}
            >
              <RefreshCwIcon size={16} />
              Refresh
            </Button>
          </>
        }
      />

      <VStack align="stretch" gap={6}>
        {/* Stats Cards */}
        {statsLoading ? (
          <Flex justify="center" py={8}>
            <Spinner />
          </Flex>
        ) : (
          <Flex gap={4} wrap="wrap">
            <Box flex="1" minW="200px">
              <StatCard
                title="Total Page Views"
                value={stats?.totalPageViews || 0}
                icon={EyeIcon}
                color="blue"
              />
            </Box>
            <Box flex="1" minW="200px">
              <StatCard
                title="External Link Clicks"
                value={stats?.totalExternalLinkClicks || 0}
                icon={ExternalLinkIcon}
                color="orange"
              />
            </Box>
            <Box flex="1" minW="200px">
              <StatCard
                title="Unique Users"
                value={stats?.uniqueUsers || 0}
                icon={UsersIcon}
                color="green"
              />
            </Box>
            <Box flex="1" minW="200px">
              <StatCard
                title="Avg Views/User"
                value={
                  stats?.uniqueUsers ? Math.round(stats.totalPageViews / stats.uniqueUsers) : 0
                }
                icon={TrendingUpIcon}
                color="purple"
              />
            </Box>
          </Flex>
        )}

        {/* Top Lists */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="Top Activity" />
          <Flex gap={6} wrap="wrap">
            <Box flex="1" minW="300px">
              <TopList
                title="Top 5 Users"
                items={stats?.topUsers || []}
                labelKey="userEmail"
                valueKey="pageViews"
                emptyMessage="No user activity yet"
                onViewAll={() => navigate({ to: '/admin/activity-users' })}
              />
            </Box>
            <Box flex="1" minW="300px">
              <TopList
                title="Top 5 Pages"
                items={stats?.topPages || []}
                labelKey="pagePath"
                valueKey="pageViews"
                emptyMessage="No page visits yet"
                onViewAll={() => navigate({ to: '/admin/activity-pages' })}
              />
            </Box>
            <Box flex="1" minW="300px">
              <TopList
                title="Top 5 External Links"
                items={stats?.topExternalLinks || []}
                labelKey="linkName"
                valueKey="clicks"
                valueLabel="clicks"
                emptyMessage="No external link clicks yet"
                onViewAll={() => navigate({ to: '/admin/activity-external-links' })}
              />
            </Box>
          </Flex>
        </VStack>

        {/* Activity Log Table */}
        <Card.Root>
          <Card.Header>
            <Heading size="sm">Recent Activity</Heading>
          </Card.Header>
          <Table.Root>
            <Table.Header>
              <Table.Row bg="bg.subtle">
                <Table.ColumnHeader>User</Table.ColumnHeader>
                <Table.ColumnHeader>Page</Table.ColumnHeader>
                <Table.ColumnHeader>Time</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {logsLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={3}>
                    <Flex justify="center" py={8}>
                      <Spinner />
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : logs.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={3}>
                    <Text textAlign="center" py={8} color="content.secondary">
                      No activity logs found
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                logs.map((log: ActivityLog) => (
                  <Table.Row key={log.id}>
                    <Table.Cell>
                      <Text fontSize="sm">{log.userEmail || 'Unknown'}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <VStack align="flex-start" gap={0}>
                        <Text fontSize="sm" fontWeight="medium">
                          {formatPath(log.pagePath)}
                        </Text>
                        <Text fontSize="xs" color="content.secondary">
                          {log.pagePath}
                        </Text>
                      </VStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{formatDate(log.createdAt)}</Text>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card.Root>

        {/* Pagination */}
        {totalPages > 1 && (
          <Flex justify="space-between" align="center">
            <Text fontSize="sm" color="content.secondary">
              Showing {(currentPage - 1) * PAGE_SIZE + 1}-
              {Math.min(currentPage * PAGE_SIZE, totalLogs)} of {totalLogs} logs
            </Text>
            <HStack gap={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeftIcon size={16} />
                Previous
              </Button>
              <HStack gap={1}>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page: number
                  if (totalPages <= 5) {
                    page = i + 1
                  } else if (currentPage <= 3) {
                    page = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i
                  } else {
                    page = currentPage - 2 + i
                  }
                  return (
                    <Button
                      key={page}
                      size="sm"
                      variant={page === currentPage ? 'solid' : 'outline'}
                      colorPalette={page === currentPage ? 'blue' : undefined}
                      onClick={() => setCurrentPage(page)}
                      minW="40px"
                    >
                      {page}
                    </Button>
                  )
                })}
              </HStack>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRightIcon size={16} />
              </Button>
            </HStack>
          </Flex>
        )}
      </VStack>
    </Box>
  )
}
